import Modal from "../../../../../assets/js/components/Modal";
import {
	buildTeamGroupUpsertPayload,
	canRemoveEntityFromTeamGroup,
	isClubDirectlyInTeamGroup,
	isTeamDirectlyInTeamGroup,
} from "features/teamGroup/teamGroupUpsertUtils";
import client from "libs/client";
import { parseMessageAttribute } from "libs/utils";
import { useMemo, useState, type FormEvent } from "react";
import { Link } from "react-router";
import { PaginationBar } from "ui-kit/src/PaginationControls/PaginationBar";
import { useToast } from "ui-kit";
import type { TeamGroupRowResponse } from "libs/apiClient";

const PAGE_SIZE = 20;

function renderMultilineCell(value: string | null | undefined): string {
	if (value == null || value === "") {
		return "-";
	}

	return value;
}

type DiscoverTeamGroupsTableProps = {
	teamGroupEditAccess: boolean;
} & (
	| { teamId: number; clubId?: never }
	| { clubId: number; teamId?: never }
);

export function DiscoverTeamGroupsTable({
	teamId,
	clubId,
	teamGroupEditAccess,
}: DiscoverTeamGroupsTableProps) {
	const { addSuccessToast, addErrorToast } = useToast();
	const [pageIndex, setPageIndex] = useState(0);
	const [removeTarget, setRemoveTarget] = useState<TeamGroupRowResponse | null>(null);
	const [isAddModalOpen, setIsAddModalOpen] = useState(false);
	const [selectedTeamGroupId, setSelectedTeamGroupId] = useState("");
	const [isSubmitting, setIsSubmitting] = useState(false);

	const filterField = teamId !== undefined ? "teamId" : "clubId";
	const entityId = teamId ?? clubId ?? 0;

	const queryParams = useMemo(
		() => ({
			page: pageIndex + 1,
			limit: PAGE_SIZE,
			filters: [
				{
					field: filterField,
					type: "inarray" as const,
					value: [entityId],
				},
			],
			order: { field: "id", direction: "DESC" as const },
		}),
		[entityId, filterField, pageIndex],
	);

	const { data: teamGroupsResponse, isLoading, refetch } = client.getTeamGroups.useQuery(queryParams, {
		retry: false,
	});

	const teamGroups = teamGroupsResponse?.data?.items ?? [];
	const pageCount = teamGroupsResponse?.data?.pages ?? 0;
	const total = teamGroupsResponse?.data?.total ?? 0;

	const { data: allTeamGroupsResponse, isLoading: isAllTeamGroupsLoading } = client.loadTeamGroups.useQuery(
		{},
		{
			enabled: isAddModalOpen && teamGroupEditAccess,
			retry: false,
		},
	);

	const allLinkedTeamGroupsQueryParams = useMemo(
		() => ({
			page: 1,
			limit: total > 0 ? total : 1,
			filters: [
				{
					field: filterField,
					type: "inarray" as const,
					value: [entityId],
				},
			],
			order: { field: "id", direction: "DESC" as const },
		}),
		[entityId, filterField, total],
	);

	const { data: allLinkedTeamGroupsResponse, isLoading: isAllLinkedTeamGroupsLoading } =
		client.getTeamGroups.useQuery(allLinkedTeamGroupsQueryParams, {
			enabled: isAddModalOpen && teamGroupEditAccess && total > 0,
			retry: false,
		});

	const putTeamGroupMutation = client.putTeamGroup.useMutation({
		onSuccess: () => {
			refetch();
		},
	});

	const linkedTeamGroupIds = useMemo(() => {
		const linkedTeamGroups = allLinkedTeamGroupsResponse?.data?.items ?? [];
		return new Set(linkedTeamGroups.map((teamGroup) => teamGroup.id));
	}, [allLinkedTeamGroupsResponse]);

	const availableTeamGroupOptions = (allTeamGroupsResponse?.data?.data ?? []).filter(
		(option) => option.value != null && linkedTeamGroupIds.has(option.value) === false,
	);

	const isAddModalDataLoading = isAllTeamGroupsLoading || (total > 0 && isAllLinkedTeamGroupsLoading);

	const canRemoveFromTeamGroup = (teamGroup: TeamGroupRowResponse): boolean => {
		if (teamId !== undefined) {
			return canRemoveEntityFromTeamGroup(teamGroup, { teamId });
		}

		if (clubId !== undefined) {
			return canRemoveEntityFromTeamGroup(teamGroup, { clubId });
		}

		return false;
	};

	const closeRemoveModal = () => {
		setRemoveTarget(null);
		setIsSubmitting(false);
	};

	const closeAddModal = () => {
		setIsAddModalOpen(false);
		setSelectedTeamGroupId("");
		setIsSubmitting(false);
	};

	const handleRemove = async () => {
		if (removeTarget === null) {
			return;
		}

		setIsSubmitting(true);

		try {
			const detailResponse = await client.getTeamGroup.query({ id: removeTarget.id });
			const teamGroup = detailResponse.data;

			if (teamGroup == null) {
				addErrorToast("Team group not found");
				closeRemoveModal();
				return;
			}

			if (teamId !== undefined && isTeamDirectlyInTeamGroup(teamGroup, teamId) === false) {
				addErrorToast("This team is included via club membership. Remove the club from the team group instead.");
				closeRemoveModal();
				return;
			}

			if (clubId !== undefined && isClubDirectlyInTeamGroup(teamGroup, clubId) === false) {
				addErrorToast("This club is not directly assigned to the team group.");
				closeRemoveModal();
				return;
			}

			const payload = buildTeamGroupUpsertPayload(teamGroup);

			if (teamId !== undefined) {
				payload.teamIds = payload.teamIds.filter((id) => id !== teamId);
			}

			if (clubId !== undefined) {
				payload.clubIds = payload.clubIds.filter((id) => id !== clubId);
			}

			await putTeamGroupMutation.mutateAsync({ id: removeTarget.id, body: payload });
			addSuccessToast("Removed from team group");
			closeRemoveModal();
		} catch (error: unknown) {
			addErrorToast(parseMessageAttribute(error) || "Failed to remove from team group");
			setIsSubmitting(false);
		}
	};

	const handleAdd = async (event: FormEvent<HTMLFormElement>) => {
		event.preventDefault();

		if (selectedTeamGroupId === "") {
			return;
		}

		setIsSubmitting(true);

		try {
			const teamGroupId = Number(selectedTeamGroupId);
			const detailResponse = await client.getTeamGroup.query({ id: teamGroupId });
			const teamGroup = detailResponse.data;

			if (teamGroup == null) {
				addErrorToast("Team group not found");
				setIsSubmitting(false);
				return;
			}

			const payload = buildTeamGroupUpsertPayload(teamGroup);

			if (teamId !== undefined && payload.teamIds.includes(teamId) === false) {
				payload.teamIds = [...payload.teamIds, teamId];
			}

			if (clubId !== undefined && payload.clubIds.includes(clubId) === false) {
				payload.clubIds = [...payload.clubIds, clubId];
			}

			await putTeamGroupMutation.mutateAsync({ id: teamGroupId, body: payload });
			addSuccessToast("Added to team group");
			closeAddModal();
		} catch (error: unknown) {
			addErrorToast(parseMessageAttribute(error) || "Failed to add to team group");
			setIsSubmitting(false);
		}
	};

	if (isLoading) {
		return <div className="empty-sub-table">Loading...</div>;
	}

	return (
		<>
			{teamGroupEditAccess && (
				<div style={{ marginBottom: "1rem", display: "flex", justifyContent: "flex-end" }}>
					<button type="button" onClick={() => setIsAddModalOpen(true)}>
						Add to team group
					</button>
				</div>
			)}

			{total === 0 ? (
				<div className="empty-sub-table">No team groups</div>
			) : (
				<>
					<div className="discover-team-groups-table">
						<table className="discover-table">
							<thead>
								<tr>
									<th>ID</th>
									<th>Name</th>
									<th>Clubs</th>
									<th>Direct teams</th>
									<th>All teams</th>
									{teamGroupEditAccess && <th>Actions</th>}
								</tr>
							</thead>
							<tbody>
								{teamGroups.map((teamGroup) => (
									<tr key={teamGroup.id}>
										<td>
											<Link to={`/team-group/${teamGroup.id}`}>{teamGroup.id}</Link>
										</td>
										<td>
											<Link to={`/team-group/${teamGroup.id}`}>{teamGroup.name}</Link>
										</td>
										<td>
											<div style={{ whiteSpace: "pre-line" }}>
												{renderMultilineCell(teamGroup.clubNames)}
											</div>
										</td>
										<td>
											<div style={{ whiteSpace: "pre-line" }}>
												{renderMultilineCell(teamGroup.directTeamNames)}
											</div>
										</td>
										<td>
											<div style={{ whiteSpace: "pre-line" }}>
												{renderMultilineCell(teamGroup.allTeamNames)}
											</div>
										</td>
										{teamGroupEditAccess && (
											<td>
												{canRemoveFromTeamGroup(teamGroup) ? (
													<button
														type="button"
														className="button-link"
														onClick={() => setRemoveTarget(teamGroup)}
													>
														Remove
													</button>
												) : (
													"-"
												)}
											</td>
										)}
									</tr>
								))}
							</tbody>
						</table>
					</div>
					<PaginationBar
						totalPages={pageCount}
						currentPage={pageIndex + 1}
						onPageChange={(page) => setPageIndex(page - 1)}
					/>
				</>
			)}

			{removeTarget !== null && (
				<Modal
					show={true}
					title="Remove from team group"
					size="sm"
					onClose={() => isSubmitting === false && closeRemoveModal()}
				>
					<p style={{ marginBottom: "1.5rem" }}>
						Do you want to remove this {teamId !== undefined ? "team" : "club"} from team group{" "}
						<strong>{removeTarget.name}</strong>?
					</p>
					<div className="admin-buttons admin-buttons--right">
						<button
							type="button"
							className="admin-button admin-button--primary"
							disabled={isSubmitting}
							onClick={handleRemove}
						>
							Remove
						</button>
						<button
							type="button"
							className="admin-button admin-button--secondary"
							disabled={isSubmitting}
							onClick={closeRemoveModal}
						>
							Cancel
						</button>
					</div>
				</Modal>
			)}

			{isAddModalOpen && (
				<Modal
					show={true}
					title="Add to team group"
					size="sm"
					onClose={() => isSubmitting === false && closeAddModal()}
				>
					<form onSubmit={handleAdd}>
						<div style={{ display: "flex", flexDirection: "column", gap: "1rem" }}>
							<div style={{ display: "flex", alignItems: "center", gap: "1rem" }}>
								<label htmlFor="team-group-select" style={{ width: "120px" }}>
									Team group <span style={{ color: "red" }}>*</span>
								</label>
								<select
									id="team-group-select"
									value={selectedTeamGroupId}
									onChange={(event) => setSelectedTeamGroupId(event.target.value)}
									required
									disabled={isAddModalDataLoading || isSubmitting}
									style={{ flex: 1 }}
								>
									<option value="">
										{isAddModalDataLoading ? "Loading..." : "Select team group"}
									</option>
									{availableTeamGroupOptions.map((option) => (
										<option key={option.value} value={option.value}>
											{option.label}
										</option>
									))}
								</select>
							</div>
						</div>
						<div className="admin-buttons admin-buttons--right">
							<button
								type="submit"
								className="admin-button admin-button--primary"
								disabled={isSubmitting || isAddModalDataLoading || availableTeamGroupOptions.length === 0}
							>
								Add to team group
							</button>
							<button
								type="button"
								className="admin-button admin-button--secondary"
								disabled={isSubmitting}
								onClick={closeAddModal}
							>
								Cancel
							</button>
						</div>
					</form>
				</Modal>
			)}
		</>
	);
}
